/**
 * The motion box of a motion period: where in the frame the motion was, from the heatmap rows of
 * the period. One member for each period, read by its card while the pointer is on it.
 *
 * A closed period always gives the same box, but the open period grows at each poll of the list,
 * and its new end is a new member, so the box follows the motion that goes on.
 */

import { type Atom, atom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import { getScopeKey } from '@/lib/scopeKey';
import { fetchMotionBox } from '@/panels/motion_list/motionBoxSlices';
import type { MotionBoxRequest, MotionBoxSnapshot } from '@/types/heatmap';
import type { NormalizedRect } from '@/types/player';

const PENDING: MotionBoxSnapshot = { box: null, isLoading: true, error: null };

const periods = queryAtoms<MotionBoxRequest, MotionBoxSnapshot, NormalizedRect | null>({
    getRequestKey: (request): string => getScopeKey({ ...request }),
    getPending: (): MotionBoxSnapshot => PENDING,
    load: ({ cameraId, startMs, endMs }): Promise<NormalizedRect | null> =>
        fetchMotionBox(api, cameraId, startMs, endMs),
    merge: (_previousSnapshot, box): MotionBoxSnapshot => ({ box, isLoading: false, error: null }),
});

const noBoxAtom = atom<MotionBoxSnapshot>({ box: null, isLoading: false, error: null });

/** The box of the period of `request`; with no request, no box and nothing loads. */
export function motionBoxAtom(request: MotionBoxRequest | null): Atom<MotionBoxSnapshot> {
    return request === null ? noBoxAtom : periods.getMember(request);
}
